import { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import api from "../api/axios";

export default function ResetPassword() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setMessage("");
    setError("");

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    setIsSubmitting(true);
    try {
      const res = await api.post(`/auth/reset-password/${token}`, {
        password,
      });

      const data = res.data;
      if (res.status === 200) {
        setMessage(data.message || "Password reset successful");
        setTimeout(() => navigate("/login"), 2000);
      } else {
        setError(data.message || "Something went wrong");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Server error. Try again later.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="relative min-h-[calc(100vh-72px)] flex justify-center items-center bg-slate-950 px-4 py-10">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(37,99,235,0.35),_transparent_35%),radial-gradient(circle_at_bottom_right,_rgba(250,204,21,0.18),_transparent_30%)]" />
      <form
        onSubmit={handleSubmit}
        className="relative w-full max-w-md rounded-[2rem] border border-white/15 bg-slate-900/75 p-8 text-white shadow-2xl backdrop-blur-xl"
      >
        <p className="mx-auto mb-4 inline-flex rounded-full border border-cyan-300/35 bg-cyan-300/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-cyan-200">
          Account security
        </p>
        <h2 className="mb-2 text-center text-2xl font-bold text-white">Create a new password</h2>
        <p className="mb-5 text-center text-sm leading-6 text-slate-300">
          Set a strong password that you have not used on another platform.
        </p>

        <div className="mb-4">
          <label className="mb-2 block text-sm font-semibold text-slate-200">New password</label>
          <div className="flex items-center rounded-xl border border-white/15 bg-white/5 px-3 py-2 focus-within:ring-2 focus-within:ring-cyan-300/35">
            <input
              type={showPassword ? "text" : "password"}
              placeholder="********"
              className="w-full bg-transparent text-white placeholder:text-slate-400 outline-none"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            <button
              type="button"
              className="ml-2 text-slate-300 hover:text-white"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>
        </div>
        <p className="-mt-2 mb-3 text-xs text-slate-400">Use at least 8 characters with numbers and letters.</p>

        <div className="mb-4">
          <label className="mb-2 block text-sm font-semibold text-slate-200">Confirm new password</label>
          <div className="flex items-center rounded-xl border border-white/15 bg-white/5 px-3 py-2 focus-within:ring-2 focus-within:ring-cyan-300/35">
            <input
              type={showConfirmPassword ? "text" : "password"}
              placeholder="********"
              className="w-full bg-transparent text-white placeholder:text-slate-400 outline-none"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
            />
            <button
              type="button"
              className="ml-2 text-slate-300 hover:text-white"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            >
              {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>
        </div>

        <button
          type="submit"
          disabled={isSubmitting}
          aria-busy={isSubmitting}
          className="w-full rounded-xl bg-accent py-3 font-semibold text-slate-950 transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-70"
        >
          {isSubmitting ? "Updating password…" : "Update password"}
        </button>

        {message && <p className="mt-3 rounded-xl border border-emerald-400/30 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-200">{message}</p>}
        {error && <p className="mt-3 rounded-xl border border-red-400/30 bg-red-500/10 px-3 py-2 text-sm text-red-200">{error}</p>}

        <p className="mt-4 text-center text-sm text-slate-300">
          Back to <Link to="/login" className="font-semibold text-cyan-300">Sign in</Link>
        </p>
      </form>
    </div>
  );
}
